/**
 * Delivery estimate shown under the buy buttons.
 * Orders dispatch Monday to Saturday before the cut-off (UK time) and arrive the next
 * working day, including Saturdays. Nothing dispatches or arrives on Sundays or
 * bank holidays.
 */

// England & Wales bank holidays (gov.uk). Add the next year's dates each December.
const BANK_HOLIDAYS = new Set([
  '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
  '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
]);

const DAY_MS = 24 * 60 * 60 * 1000;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Today's date (as a UTC midnight Date) and the time of day in the UK */
function ukNow() {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .map((part) => [part.type, part.value])
  );

  return {
    date: new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))),
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
}

/** @param {Date} date */
const isoDate = (date) => date.toISOString().slice(0, 10);

/** @param {Date} date */
const isWorkingDay = (date) => date.getUTCDay() !== 0 && !BANK_HOLIDAYS.has(isoDate(date));

/** @param {Date} date */
function nextWorkingDay(date) {
  let next = new Date(date.getTime() + DAY_MS);
  while (!isWorkingDay(next)) next = new Date(next.getTime() + DAY_MS);
  return next;
}

/** @param {Date} date */
const formatDate = (date) => `${DAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;

/** @param {number} cutoffHour */
export function deliveryEstimateText(cutoffHour) {
  const now = ukNow();
  const minutesLeft = cutoffHour * 60 - now.minutes;
  const dispatchesToday = isWorkingDay(now.date) && minutesLeft > 0;
  const dispatchDate = dispatchesToday ? now.date : nextWorkingDay(now.date);
  const deliveryDate = nextWorkingDay(dispatchDate);

  if (!dispatchesToday) return `Order now for delivery ${formatDate(deliveryDate)}`;

  const isNextDay = deliveryDate.getTime() - dispatchDate.getTime() === DAY_MS;
  const delivery = `${isNextDay ? 'next day delivery' : 'delivery'} ${formatDate(deliveryDate)}`;

  if (minutesLeft <= 180) {
    const hours = Math.floor(minutesLeft / 60);
    const minutes = minutesLeft % 60;
    return `Order within ${hours ? `${hours}h ` : ''}${minutes}m for ${delivery}`;
  }

  const cutoffLabel = cutoffHour > 12 ? `${cutoffHour - 12}pm` : `${cutoffHour}am`;
  return `Order before ${cutoffLabel} for ${delivery}`;
}

class DeliveryEstimate extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.#render();
    this.#timer = window.setInterval(() => this.#render(), 60 * 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  #render() {
    this.textContent = deliveryEstimateText(Number(this.dataset.cutoffHour) || 17);
  }
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}